import type { OrderedFiles } from '@/lib/ai/ordered-files-service/order-files-prompts';
import type { CodeContent } from '@/types/github';

import { groupFilesByOrderedFiles } from '@/lib/ai/ordered-files-service/group-ordered-files';
import { getPrV2DiffFileElementId } from '@/lib/stores/navigation/diff-file-element-ids';
import { chunkArray } from '@/lib/utils/chunk-array';

export const PR_V2_FILE_TREE_WIDTH = 272;
export const PR_V2_FILE_TREE_MIN_WIDTH = 220;
export const PR_V2_FILE_TREE_MAX_WIDTH = 520;
export const PR_V2_FILE_TREE_FONT_SIZE_PX = 13;

/** The width the file tree actually renders at, whatever the persisted value says. */
export function clampFileTreeWidth(width: number): number {
  return Math.min(PR_V2_FILE_TREE_MAX_WIDTH, Math.max(PR_V2_FILE_TREE_MIN_WIDTH, width));
}

/**
 * The Tailwind breakpoint from which the desktop tree starts open: xl is 1280 px, 2xl is
 * 1536 px. The PR page opens it from xl. A panel narrower than the screen, such as the /code
 * PR tab, waits for 2xl, because the screen width overstates the room it has.
 */
export type PrV2FileTreeOpenBreakpoint = 'xl' | '2xl';

export const PR_V2_FILE_TREE_OPEN_MEDIA_QUERY: Record<PrV2FileTreeOpenBreakpoint, string> = {
  'xl': '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
};

/**
 * Splits a file name so the tree can cut its middle and keep the suffix: a truncated
 * `foo.test.tsx` still reads differently from `foo.tsx`. A leading dot never starts the tail.
 */
export function splitPrV2FileTreeFileName(name: string): { head: string; tail: string } {
  const firstDotIndex = name.indexOf('.', 1);
  if (firstDotIndex > 0 && name.length - firstDotIndex <= 12) {
    return { head: name.slice(0, firstDotIndex), tail: name.slice(firstDotIndex) };
  }

  const lastDotIndex = name.lastIndexOf('.');
  if (lastDotIndex > 0 && name.length - lastDotIndex <= 10) {
    return { head: name.slice(0, lastDotIndex), tail: name.slice(lastDotIndex) };
  }

  return { head: name, tail: '' };
}

/**
 * Splits a folder label (a compacted chain like `src/app/pr-v2`) before its last segment, so the
 * tree can cut the middle and keep both where the chain starts and the folder it ends in.
 */
export function splitPrV2FileTreeFolderLabel(label: string): { head: string; tail: string } {
  const lastSlashIndex = label.lastIndexOf('/');
  if (lastSlashIndex <= 0) {
    return { head: label, tail: '' };
  }

  return { head: label.slice(0, lastSlashIndex), tail: label.slice(lastSlashIndex) };
}

export function normalizePrV2TreeId(value: string): string {
  return value.replace(/[^\w/-]/g, '-').replaceAll('/', '--');
}

export function getPrV2FileElementId(filename: string): string {
  return getPrV2DiffFileElementId(filename);
}

export function getPrV2GroupElementId(groupName: string, groupOrder: number): string {
  return `pr-v2-group-${groupOrder}-${normalizePrV2TreeId(groupName)}`;
}

export function getPrV2ChapterKey(groupName: string, groupOrder: number): string {
  return `${groupOrder}:${groupName}`;
}

export type PrV2GroupedCodeContent = {
  groupName: string;
  groupOrder: number;
  groupNameReason?: string;
  files: CodeContent[];
};

export const PR_V2_CHANGED_FILES_GROUP_NAME = 'Changed files';
export const PR_V2_CHAPTER_MODE_FILE_THRESHOLD = 50;

function isDeterministicOrderedFilesFallback(orderedFiles: OrderedFiles[]): boolean {
  return (
    orderedFiles.length > 0 &&
    orderedFiles.every((file) => file.groupNameReason?.includes('AI ordering was unavailable'))
  );
}

export function hasUsablePrV2OrderedFiles(orderedFiles: OrderedFiles[]): boolean {
  return orderedFiles.length > 0 && !isDeterministicOrderedFilesFallback(orderedFiles);
}

function splitCodeContentIntoPages(codeContent: CodeContent[]): PrV2GroupedCodeContent[] {
  // The fewest pages within the threshold, filled evenly so 51 files become 26 and 25, not 50 and 1.
  const pageCount = Math.ceil(codeContent.length / PR_V2_CHAPTER_MODE_FILE_THRESHOLD);
  const pageSize = Math.ceil(codeContent.length / pageCount);

  return chunkArray(codeContent, pageSize).map((files, pageIndex) => {
    const firstFileNumber = pageIndex * pageSize + 1;

    return {
      groupName: `Files ${firstFileNumber}–${firstFileNumber + files.length - 1}`,
      groupOrder: pageIndex + 1,
      groupNameReason: `More than ${PR_V2_CHAPTER_MODE_FILE_THRESHOLD} files changed, so they're split into chapters.`,
      files,
    };
  });
}

export function groupCodeContent(
  codeContent: CodeContent[],
  orderedFiles: OrderedFiles[],
): PrV2GroupedCodeContent[] {
  if (!hasUsablePrV2OrderedFiles(orderedFiles)) {
    // Chapter mode only paginates PRs with more than one group, so split a large PR without
    // AI groups into pages rather than mount all of its files in one group.
    if (codeContent.length > PR_V2_CHAPTER_MODE_FILE_THRESHOLD) {
      return splitCodeContentIntoPages(codeContent);
    }

    return [
      {
        groupName: PR_V2_CHANGED_FILES_GROUP_NAME,
        groupOrder: 1,
        files: codeContent,
      },
    ];
  }

  return groupFilesByOrderedFiles(codeContent, orderedFiles);
}
